import React, { useState, useEffect } from 'react';

const WhatsAppButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const phoneNumber = "255759549641";
  const defaultMessage = "Hello, I would like more information.";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`;

  useEffect(() => {
    // Entrance animation delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    // Show tooltip after button appears
    const tooltipTimer = setTimeout(() => {
      setShowTooltip(true);
      setHasAnimated(true);
      // Hide tooltip after 5 seconds
      setTimeout(() => setShowTooltip(false), 5000);
    }, 2500);

    return () => {
      clearTimeout(timer);
      clearTimeout(tooltipTimer);
    };
  }, []);

  const handleClick = () => {
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleMouseEnter = () => {
    if (hasAnimated) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (hasAnimated) {
      setShowTooltip(false);
    }
  };

  return (
    <>
      {/* WhatsApp Button Styles */}
      <style>{`
        @keyframes whatsapp-entrance {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-180deg);
          }
          50% {
            transform: scale(1.2) rotate(10deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes whatsapp-pulse {
          0%, 100% {
            box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4), 0 0 0 0 rgba(37, 211, 102, 0.4);
          }
          50% {
            box-shadow: 0 4px 25px rgba(37, 211, 102, 0.6), 0 0 0 12px rgba(37, 211, 102, 0);
          }
        }

        @keyframes tooltip-entrance {
          0% {
            opacity: 0;
            transform: translateX(10px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        .whatsapp-button-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9998;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .whatsapp-tooltip {
          background: linear-gradient(135deg, #1e3a5f 0%, #2a4a6f 100%);
          color: #ffffff;
          padding: 12px 18px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          animation: tooltip-entrance 0.4s ease-out forwards;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .whatsapp-tooltip::after {
          content: '';
          position: absolute;
          right: -8px;
          top: 50%;
          transform: translateY(-50%);
          border: 8px solid transparent;
          border-left-color: #2a4a6f;
          border-right: none;
        }

        .whatsapp-button {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          animation: whatsapp-entrance 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
                     whatsapp-pulse 2s ease-in-out infinite 1s;
          position: relative;
          overflow: hidden;
        }

        .whatsapp-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%);
          border-radius: 50%;
        }

        .whatsapp-button:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 30px rgba(37, 211, 102, 0.6);
          animation: float 1s ease-in-out infinite;
        }

        .whatsapp-button:active {
          transform: scale(0.95);
        }

        .whatsapp-button svg {
          width: 32px;
          height: 32px;
          fill: #ffffff;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
          position: relative;
          z-index: 1;
        }

        .whatsapp-button:hover svg {
          animation: none;
        }

        /* Notification badge */
        .whatsapp-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 18px;
          height: 18px;
          background: #ff4444;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          color: white;
          border: 2px solid white;
          animation: whatsapp-pulse 1.5s ease-in-out infinite;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .whatsapp-button-container {
            bottom: 20px;
            right: 20px;
          }

          .whatsapp-button {
            width: 56px;
            height: 56px;
          }

          .whatsapp-button svg {
            width: 28px;
            height: 28px;
          }

          .whatsapp-tooltip {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .whatsapp-button-container {
            bottom: 16px;
            right: 16px;
          }

          .whatsapp-button {
            width: 52px;
            height: 52px;
          }

          .whatsapp-button svg {
            width: 26px;
            height: 26px;
          }
        }

        /* Accessibility */
        .whatsapp-button:focus {
          outline: 3px solid #25D366;
          outline-offset: 3px;
        }

        .whatsapp-button:focus:not(:focus-visible) {
          outline: none;
        }

        /* Prevent overlap with other fixed elements */
        @media (max-width: 768px) {
          .whatsapp-button-container {
            bottom: 80px;
          }
        }
      `}</style>

      {/* WhatsApp Button */}
      {isVisible && (
        <div className="whatsapp-button-container">
          {/* Tooltip */}
          {showTooltip && (
            <div className="whatsapp-tooltip">
              💬 Chat with Us on WhatsApp
            </div>
          )}

          {/* Button */}
          <button
            className="whatsapp-button"
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            aria-label="Chat with us on WhatsApp"
            title="Chat with us on WhatsApp"
            data-testid="whatsapp-chat-button"
          >
            {/* WhatsApp Icon */}
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            
            {/* Optional notification badge - uncomment if needed */}
            {/* <span className="whatsapp-badge">1</span> */}
          </button>
        </div>
      )}
    </>
  );
};

export default WhatsAppButton;
